import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Phone, Lock, Save, Package, LogOut, Calendar, FileText, Bell, Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authService, wishlistService } from '../services/api';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user, isAuthenticated, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [wishlistCount, setWishlistCount] = useState(0);

  // Form states
  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    birth_date: '',
    gender: '',
    document_type: 'DNI',
    document_number: '',
    bio: '',
    newsletter_subscribed: false
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    // Esperar a que termine de cargar la autenticación
    if (loading) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (user) {
      setProfileData({
        name: user.name || '',
        phone: user.phone || '',
        birth_date: user.birth_date || '',
        gender: user.gender || '',
        document_type: user.document_type || 'DNI',
        document_number: user.document_number || '',
        bio: user.bio || '',
        newsletter_subscribed: user.newsletter_subscribed || false
      });
    }

    // Cargar cantidad de favoritos
    const fetchWishlistCount = async () => {
      try {
        const response = await wishlistService.get();
        setWishlistCount(response.data.items?.length || 0);
      } catch (error) {
        console.error('Error fetching wishlist count:', error);
      }
    };

    if (isAuthenticated) {
      fetchWishlistCount();
    }
  }, [isAuthenticated, user, navigate]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authService.updateProfile(profileData);

      if (response.data.user) {
        updateUser(response.data.user);
        toast.success('Perfil actualizado exitosamente');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.error || 'Error al actualizar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await authService.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
        confirm_password: passwordData.confirm_password
      });

      toast.success('Contraseña cambiada exitosamente');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.error || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Sesión cerrada exitosamente');
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2d3c5d] to-[#3d4c6d] rounded-2xl shadow-xl p-8 mb-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
              <User className="w-10 h-10" />
            </div>
            <div>
              <h1 className="text-3xl font-bold mb-2">Mi Perfil</h1>
              <p className="text-white/80 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {user?.email}
              </p>
              {user?.age && (
                <p className="text-white/80 flex items-center gap-2 mt-1">
                  <Calendar className="w-4 h-4" />
                  {user.age} años
                </p>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <Link
              to="/mis-ordenes"
              className="bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/20 transition-all"
            >
              <div className="flex items-center gap-3">
                <Package className="w-8 h-8" />
                <div>
                  <p className="text-sm text-white/70">Mis Órdenes</p>
                  <p className="text-2xl font-bold">{user?.stats?.total_orders || 0}</p>
                </div>
              </div>
            </Link>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8" />
                <div>
                  <p className="text-sm text-white/70">Miembro desde</p>
                  <p className="text-lg font-semibold">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString('es-AR') : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-md mb-6 overflow-hidden">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 min-w-max px-6 py-4 font-semibold transition-all ${
                activeTab === 'profile'
                  ? 'bg-[#2d3c5d] text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <User className="w-5 h-5 inline mr-2" />
              Información Personal
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`flex-1 min-w-max px-6 py-4 font-semibold transition-all ${
                activeTab === 'password'
                  ? 'bg-[#2d3c5d] text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Lock className="w-5 h-5 inline mr-2" />
              Cambiar Contraseña
            </button>
          </div>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-xl shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <User className="w-6 h-6 text-[#2d3c5d]" />
              Información Personal
            </h2>

            <form onSubmit={handleProfileUpdate} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d3c5d] focus:border-transparent transition-all"
                  required
                />
              </div>

              {/* Email (readonly) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-4 py-3 pl-11 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 mt-1">El email no puede ser modificado</p>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Teléfono
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    className="w-full px-4 py-3 pl-11 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d3c5d] focus:border-transparent transition-all"
                    placeholder="2664123456"
                  />
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
              </div>

              {/* Birth Date & Gender */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Fecha de Nacimiento
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={profileData.birth_date}
                      onChange={(e) => setProfileData({ ...profileData, birth_date: e.target.value })}
                      className="w-full px-4 py-3 pl-11 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d3c5d] focus:border-transparent transition-all"
                      max={new Date().toISOString().split('T')[0]}
                    />
                    <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Debes tener al menos 13 años</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Género
                  </label>
                  <select
                    value={profileData.gender}
                    onChange={(e) => setProfileData({ ...profileData, gender: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d3c5d] focus:border-transparent transition-all"
                  >
                    <option value="">Seleccionar género</option>
                    <option value="male">Masculino</option>
                    <option value="female">Femenino</option>
                    <option value="other">Otro</option>
                    <option value="prefer_not_to_say">Prefiero no decir</option>
                  </select>
                </div>
              </div>

              {/* Document */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tipo de Documento
                  </label>
                  <select
                    value={profileData.document_type}
                    onChange={(e) => setProfileData({ ...profileData, document_type: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d3c5d] focus:border-transparent transition-all"
                  >
                    <option value="DNI">DNI</option>
                    <option value="CUIL">CUIL</option>
                    <option value="CUIT">CUIT</option>
                    <option value="Pasaporte">Pasaporte</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Número de Documento
                  </label>
                  <input
                    type="text"
                    value={profileData.document_number}
                    onChange={(e) => setProfileData({ ...profileData, document_number: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d3c5d] focus:border-transparent transition-all"
                    placeholder="35123456"
                  />
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#2d3c5d]" />
                  Biografía
                </label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d3c5d] focus:border-transparent transition-all"
                  rows="4"
                  placeholder="Cuéntanos algo sobre ti..."
                  maxLength="500"
                />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {profileData.bio.length}/500 caracteres
                </p>
              </div>

              {/* Newsletter */}
              <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={profileData.newsletter_subscribed}
                    onChange={(e) => setProfileData({ ...profileData, newsletter_subscribed: e.target.checked })}
                    className="w-5 h-5 text-[#2d3c5d] border-gray-300 rounded focus:ring-[#2d3c5d] cursor-pointer"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Bell className="w-5 h-5 text-[#2d3c5d]" />
                    <div>
                      <p className="font-semibold text-gray-900">Suscribirse al Newsletter</p>
                      <p className="text-sm text-gray-600">Recibí ofertas, promociones y novedades por email</p>
                    </div>
                  </div>
                </label>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-[#2d3c5d] to-[#3d4c6d] text-white py-3.5 rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      Guardar Cambios
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="px-6 py-3.5 rounded-xl font-semibold border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-all flex items-center gap-2"
                >
                  <LogOut className="w-5 h-5" />
                  Cerrar Sesión
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <div className="bg-white rounded-xl shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Lock className="w-6 h-6 text-[#2d3c5d]" />
              Cambiar Contraseña
            </h2>

            <form onSubmit={handlePasswordChange} className="space-y-6 max-w-2xl">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Contraseña Actual *
                </label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d3c5d] focus:border-transparent transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nueva Contraseña *
                </label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d3c5d] focus:border-transparent transition-all"
                  required
                  minLength="6"
                />
                <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirmar Nueva Contraseña *
                </label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...profileData, confirm_password: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d3c5d] focus:border-transparent transition-all"
                  required
                  minLength="6"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#2d3c5d] to-[#3d4c6d] text-white py-3.5 rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Cambiando...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Cambiar Contraseña
                  </>
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
